import {
  buildReclaimReferralCodeIntakeAnswers,
  hasReclaimReferralCodeIntakeOutcome,
  isReclaimReferralCodeIntakeComplete,
} from "@redotech/redo-dispute-common/reclaim-referral-code-intake";
import {
  getReclaimShopifyPermissionsAuthorizationUrl,
  hasReclaimShopifyPermissions,
  RECLAIM_SHOPIFY_PERMISSIONS_COPY,
  RECLAIM_SHOPIFY_PERMISSIONS_STEP_ID,
} from "../../../payments/chargebacks/disputes-shopify-permissions";
import { unifiedOnboardingProductPath } from "../../../unified-onboarding/chargebacks-entry";
import {
  hasStartedReclaim,
  isReclaimOnboarded,
} from "../../../unified-onboarding/products/chargeback-recovery/is-onboarded";
import type {
  ProductSetupConfig,
  SetupTask,
  SetupTaskGroup,
} from "../../registry/types";
import {
  FINISH_RECLAIM_SETUP_TASK_ID,
  RECLAIM_WALKTHROUGH_TASK_ID,
} from "./constants";
import {
  RECLAIM_REFERRAL_CODE_STEP_ID,
  ReclaimReferralCodeStep,
  writeReclaimReferralCodeAnswers,
} from "./reclaim-referral-code-step";

/**
 * Disputes/Reclaim setup tasks.
 *
 * Two tasks. Activation, billing and AI automation all happen inside unified
 * onboarding, so the checklist links back into that flow rather than offering
 * its own routes to them — the old per-step tasks reached those settings
 * directly and could turn automation on without stamping commercial terms.
 *
 * "Finish setting up Reclaim" is also the only place an incomplete setup is
 * visible: a merchant who accepts billing outside the flow can end up paying
 * with automation off, and the billing banner disappears once nothing is owed.
 * Evidence window stays at the system default (5 days) for the ~80% of
 * merchants it works for; merchants with long fulfillment cycles can tune it
 * on the settings page. Keeping it out of the checklist avoids a click for
 * almost everyone.
 *
 * `disputes/enabled` itself is no longer a task — intake submit flips it
 * using the current Reclaim access defaults, so by the time the widget
 * renders the legacy "Set up Reclaim" predicate is already satisfied.
 */

const FINISH_RECLAIM_SETUP: SetupTask = {
  id: FINISH_RECLAIM_SETUP_TASK_ID,
  title: "Finish setting up Reclaim",
  description:
    "Pick up where you left off — connect your dispute sources and turn on automatic evidence.",
  actionLabel: "Finish setup",
  // No surface: this renders for any team with the guide, so it cannot vouch
  // for the channel the way a specific CTA can, and terms are already stamped
  // for everyone it shows to.
  route: (teamId) =>
    unifiedOnboardingProductPath({ teamId, productId: "chargeback_recovery" }),
  // Only once they have begun: a merchant who has never started sees the
  // insights CTA instead, which does know its channel.
  visiblePredicate: hasStartedReclaim,
  completionPredicate: isReclaimOnboarded,
  requiredForLive: true,
};

const TAKE_RECLAIM_WALKTHROUGH: SetupTask = {
  id: RECLAIM_WALKTHROUGH_TASK_ID,
  title: "Take the Reclaim walkthrough",
  description:
    "Start with a quick tour of the dashboard, chargeback queue, evidence review, and automation controls.",
  route: (teamId) =>
    `/stores/${teamId}/payments/chargebacks/overview?walkthrough=disputes`,
  // Completed by the walkthrough's final setup-task signal.
  completionPredicate: () => false,
  requiredForLive: true,
};

const TASK_LIST = [FINISH_RECLAIM_SETUP, TAKE_RECLAIM_WALKTHROUGH];

// Single ordered group — widget renders it as a flat sequential list.
const GROUPS: SetupTaskGroup[] = [
  { id: "setup", title: "Setup", taskIds: TASK_LIST.map((t) => t.id) },
];

export const disputesProductConfig: ProductSetupConfig = {
  product: "disputes",
  displayName: "Reclaim setup",
  routePattern:
    /\/(finance\/disputes|payments\/chargebacks|settings\/chargebacks)(\/|$)/,
  resumeRoute: (storeId) => `/stores/${storeId}/payments/chargebacks/overview`,
  onIntakeSubmit: async ({ activationSurface, rpcClient }) => {
    // The channel is derived server-side from the team, except the surface,
    // which only the CTA knows. Without it the insights-page CTA resolved to
    // intake_modal (free) whenever async onboarding routed through this guide,
    // so the same button billed or waived depending on a feature flag.
    const result = await rpcClient.enableDisputes({
      activation: { surface: activationSurface ?? undefined },
    });
    if (result?.status === "billing_required") {
      const billingResult = await rpcClient.enableDisputesBilling({});
      if (billingResult.confirmationUrl) {
        window.location.href = billingResult.confirmationUrl;
      }
    }
  },
  intake: {
    additionalSteps: [
      {
        id: RECLAIM_REFERRAL_CODE_STEP_ID,
        secondaryFooterButton: {
          onClick: ({ onContinue, setAnswer }) => {
            writeReclaimReferralCodeAnswers({
              setAnswer,
              state: { status: "skipped_without_code" },
            });
            onContinue();
          },
          text: "Continue without code",
        },
        getSkippedAnswers: ({ answers, reclaimTerms }) =>
          reclaimTerms?.accessMode === "free" &&
          !hasReclaimReferralCodeIntakeOutcome(answers)
            ? buildReclaimReferralCodeIntakeAnswers({
                status: "skipped_free_terms",
              })
            : {},
        isComplete: isReclaimReferralCodeIntakeComplete,
        render: ReclaimReferralCodeStep,
        shouldInclude: ({ reclaimTerms }) =>
          reclaimTerms ? reclaimTerms.accessMode !== "free" : false,
      },
    ],
    welcome: {
      title: "Welcome to Reclaim",
      subtitle: "Let's defend your chargebacks automatically.",
      useDisputeStatsSubtitle: true,
    },
    finalInfoStep: {
      title: "Review Reclaim terms",
      body: "Your current Reclaim terms will be shown before activation.",
    },
    authorizationStep: {
      id: RECLAIM_SHOPIFY_PERMISSIONS_STEP_ID,
      title: RECLAIM_SHOPIFY_PERMISSIONS_COPY.title,
      body: RECLAIM_SHOPIFY_PERMISSIONS_COPY.body,
      ctaText: RECLAIM_SHOPIFY_PERMISSIONS_COPY.ctaText,
      developmentSkipCtaText: "Skip in local development",
      showWhenCompleteInDevelopment: true,
      completionPredicate: hasReclaimShopifyPermissions,
      getAuthorizationUrl: ({ rpcClient }) =>
        getReclaimShopifyPermissionsAuthorizationUrl({ rpcClient }),
      errorCopy: {
        missingAfterReturn: RECLAIM_SHOPIFY_PERMISSIONS_COPY.missingAfterReturn,
        urlError: RECLAIM_SHOPIFY_PERMISSIONS_COPY.urlError,
      },
    },
    dismissible: false,
  },
  groups: GROUPS,
  tasks: Object.fromEntries(TASK_LIST.map((t) => [t.id, t])),
  completion: {
    title: "Reclaim is live",
    body: "We're now defending your chargebacks automatically. You'll see updates as new disputes come in.",
    ctaText: "Got it",
  },
  relevantTeamSlice: (team) => ({
    accessScope: team.accessScope,
    disputes: team.settings?.disputes,
  }),
};
